// ---------------------------------------------------------------------------
// Boundary mapping between SpreadsheetEditor's generic QuoteColumnType and the
// narrower ScheduleColumnType used by the schedule-of-rates feature.
//
// Used by both EditScheduleOfRatesModal (modal editor) and
// CompanyScheduleOfRatesSettings (inline editor) so the two surfaces share
// a single source of truth for type constraints.
// ---------------------------------------------------------------------------

import type { ColumnConfig, QuoteColumnType } from "../SpreadsheetEditor";
import type { ScheduleColumnType } from "./schedule-of-rates.types";

export const SCHEDULE_TO_QUOTE: Record<ScheduleColumnType, QuoteColumnType> = {
	text: "response_text",
	number: "response_number",
	currency: "response_value",
	unit_of_measurement: "unit_of_measurement",
};

export const QUOTE_TO_SCHEDULE: Partial<
	Record<QuoteColumnType, ScheduleColumnType>
> = {
	response_text: "text",
	response_number: "number",
	response_value: "currency",
	unit_of_measurement: "unit_of_measurement",
};

export const ALLOWED_QUOTE_TYPES = new Set<QuoteColumnType>([
	"response_text",
	"response_number",
	"response_value",
	"unit_of_measurement",
]);

export const quoteToScheduleType = (q: QuoteColumnType): ScheduleColumnType =>
	QUOTE_TO_SCHEDULE[q] ?? "text";

export const scheduleToQuoteType = (s: ScheduleColumnType): QuoteColumnType =>
	SCHEDULE_TO_QUOTE[s];

// ---------------------------------------------------------------------------
// Default column set for a brand-new Schedule of Rates.
//
// Deliberately narrower than QuoteTable's DEFAULT_COLUMNS, which includes
// Quantity and Cost — both unsupported here (see ALLOWED_QUOTE_TYPES above)
// and would render as disabled "Not supported" columns if used as-is.
// Shared by both entry points so labels/types can't drift between them:
// CompanyScheduleOfRatesSettings (passed as useColumnManager's initial
// columns when a company has no saved schedule yet) and
// EditScheduleOfRatesModal (passed as useColumnManager's default-columns
// override, used whenever it resets to an empty column set).
//
// Exported without localIds, matching DEFAULT_COLUMNS' own shape — each
// caller assigns localIds with whichever generator it already uses.
// ---------------------------------------------------------------------------
export const SCHEDULE_DEFAULT_COLUMNS: Omit<ColumnConfig, "localId">[] = [
	{ columnType: "response_text", label: "Description of Scope", sortOrder: 1 },
	{ columnType: "unit_of_measurement", label: "Unit of Measurement", sortOrder: 2 },
	{ columnType: "response_value", label: "Rate", sortOrder: 3 },
	{ columnType: "response_text", label: "Notes/Exclusions", sortOrder: 4 },
];
